import { useMemo, useState } from 'react';
import type { GrowthMode, MonthlyGrowthPoint } from '@utils/growthSeries';

type Props = {
	title: string;
	points: MonthlyGrowthPoint[];
	mode: GrowthMode;
	color?: string;
	emptyLabel?: string;
	helpNew?: string;
	helpCumulative?: string;
	onPointClick?: (point: MonthlyGrowthPoint) => void;
};

export default function MonthlyGrowthChart({
	title,
	points,
	mode,
	color = '#198754',
	emptyLabel = 'No data yet',
	helpNew,
	helpCumulative,
	onPointClick
}: Props) {
	const [hoveredKey, setHoveredKey] = useState<string | null>(null);

	const values = useMemo(
		() => points.map((p) => (mode === 'new' ? p.newCount : p.cumulativeCount)),
		[points, mode]
	);

	const max = Math.max(...values, 1);
	const total = points.length > 0 ? points[points.length - 1].cumulativeCount : 0;
	const hovered = points.find((p) => p.key === hoveredKey) ?? null;
	const hoveredValue = hovered ? (mode === 'new' ? hovered.newCount : hovered.cumulativeCount) : null;

	if (points.length === 0) {
		return (
			<div className="border rounded p-3 h-100 bg-white">
				<div className="text-muted small fw-semibold mb-2">{title}</div>
				<p className="text-muted small mb-0">{emptyLabel}</p>
			</div>
		);
	}

	return (
		<div className="border rounded p-3 h-100 bg-white">
			<div className="d-flex justify-content-between align-items-start gap-2 mb-2">
				<div>
					<div className="fw-semibold text-success-emphasis">{title}</div>
					<div className="text-muted small">
						{mode === 'new' ? helpNew : helpCumulative}
					</div>
				</div>
				<div className="text-end">
					<div className="text-muted small text-uppercase" style={{ fontSize: '0.65rem' }}>
						{mode === 'new' ? 'Total' : 'Latest'}
					</div>
					<div className="fs-5 fw-bold" style={{ color }}>
						{hoveredValue !== null ? hoveredValue : mode === 'new' ? values.reduce((a, b) => a + b, 0) : total}
					</div>
					{hovered && (
						<div className="text-muted small">{hovered.label}</div>
					)}
				</div>
			</div>

			<div
				className="position-relative mt-2"
				style={{ height: 200 }}
				onMouseLeave={() => setHoveredKey(null)}
			>
				{hovered && (
					<div
						className="position-absolute top-0 start-50 translate-middle-x badge text-bg-dark text-nowrap shadow-sm pe-none"
						style={{ fontSize: '0.7rem', zIndex: 3 }}
					>
						{hovered.label}: {mode === 'new' ? `${hovered.newCount} new` : `${hovered.cumulativeCount} total`}
					</div>
				)}

				{/* horizontal grid */}
				<div className="position-absolute top-0 start-0 end-0 bottom-0 d-flex flex-column justify-content-between pe-none">
					{[0, 0.5, 1].map((frac) => (
						<div
							key={frac}
							className="border-top border-light w-100"
							style={{ opacity: frac === 1 ? 0 : 1 }}
						/>
					))}
				</div>

				<div
					className="d-flex align-items-end gap-1 h-100 position-relative"
					style={{ paddingBottom: 28 }}
					role="list"
					aria-label={title}
				>
					{points.map((point, index) => {
						const value = values[index];
						const heightPct = Math.max(value === 0 ? 2 : (value / max) * 100, value > 0 ? 8 : 2);
						const isHovered = hoveredKey === point.key;
						return (
							<div
								key={point.key}
								className="flex-fill d-flex flex-column align-items-center justify-content-end h-100"
								style={{ minWidth: 0 }}
								role="listitem"
								onMouseEnter={() => setHoveredKey(point.key)}
								onFocus={() => setHoveredKey(point.key)}
								onBlur={() => setHoveredKey(null)}
								onKeyDown={(event) => {
									if ((event.key === 'Enter' || event.key === ' ') && onPointClick) {
										event.preventDefault();
										onPointClick(point);
									}
								}}
								tabIndex={0}
							>
								<div
									className="rounded-top w-100 transition-all"
									style={{
										height: `${heightPct}%`,
										backgroundColor: color,
										opacity: isHovered ? 1 : 0.72,
										boxShadow: isHovered ? `0 0 0 2px ${color}33` : 'none',
										cursor: 'pointer',
										maxWidth: 48,
										margin: '0 auto'
									}}
									title={`${point.label}: ${mode === 'new' ? point.newCount : point.cumulativeCount}`}
									onClick={() => onPointClick?.(point)}
								/>
							</div>
						);
					})}
				</div>

				<div
					className="position-absolute start-0 end-0 d-flex gap-1"
					style={{ bottom: 0, height: 24, fontSize: '0.65rem' }}
				>
					{points.map((point, index) => {
						const showLabel =
							points.length <= 8 || index === 0 || index === points.length - 1 || index % 2 === 0;
						return (
							<div
								key={`${point.key}-label`}
								className="flex-fill text-center text-muted text-truncate"
								style={{ minWidth: 0 }}
								title={point.label}
							>
								{showLabel ? point.shortLabel : ''}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
